import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, Platform, Modal, Dimensions, TextInput, Clipboard, Alert } from 'react-native';
import Icon from '@expo/vector-icons/MaterialIcons';
import { httpsCallable } from 'firebase/functions';
import { getFirestore, collection, query, orderBy, limit, getDocs, Timestamp, where, onSnapshot } from '../services/cachedFirestore';
import { functions } from '../config/firebase';
import { fonts } from '../utils/fonts';

interface NotificationStats {
  totalUsersMatched: number;
  pushNotificationsSent: number;
  byCategory?: { [category: string]: { jobs: number; usersMatched: number } };
}

interface ChannelDetail {
  channelId: string;
  channelUsername: string;
  channelName: string;
  messagesProcessed: number;
  jobsExtracted: number;
  errors: string[];
}

interface ScrapeReport {
  id: string;
  sessionId: string;
  type: 'manual' | 'scheduled';
  success: boolean;
  status: string;
  totalChannels: number;
  processedChannels: number;
  totalJobsExtracted: number;
  totalMessagesProcessed: number;
  errors: string[];
  errorCount: number;
  duration: number; // in seconds
  startedAt?: Timestamp;
  completedAt?: Timestamp;
  createdAt?: Timestamp;
  digestEmailsSent?: number;
  digestEmailErrors?: number;
  notificationStats?: NotificationStats | null;
  batchNumber?: number;
  totalBatches?: number;
  channelDetails?: ChannelDetail[];
  isRunning?: boolean;
  currentChannel?: string | null;
  nextSessionId?: string | null;
  overallTotalChannels?: number;
}

const lightColors = {
  white: '#FFFFFF',
  lightGray: '#F5F5F5',
  deepNavy: '#1A1B3E',
  warmGray: '#6B6B6B',
  beeYellow: '#F4C430',
  success: '#27AE60',
  danger: '#E74C3C',
  warning: '#F39C12',
};

const darkColors = {
  white: '#1F2937',
  lightGray: '#111827',
  deepNavy: '#F9FAFB',
  warmGray: '#9CA3AF',
  beeYellow: '#FCD34D',
  success: '#10B981',
  danger: '#EF4444',
  warning: '#F59E0B',
};

interface ScrapeHistoryContentProps {
  colors?: any;
  isDarkMode?: boolean;
}

export default function ScrapeHistoryContent({ colors = lightColors, isDarkMode = false }: ScrapeHistoryContentProps) {
  const [reports, setReports] = useState<ScrapeReport[]>([]);
  const [runningReports, setRunningReports] = useState<ScrapeReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState<ScrapeReport | null>(null);
  const [expandedReports, setExpandedReports] = useState<Set<string>>(new Set());
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [dimensions, setDimensions] = useState(Dimensions.get('window'));
  const [channelSort, setChannelSort] = useState<'jobs' | 'errors' | 'alpha'>('jobs');
  const [channelFilterText, setChannelFilterText] = useState('');
  const [expandedChannelErrors, setExpandedChannelErrors] = useState<Set<string>>(new Set());
  const [copiedSessionId, setCopiedSessionId] = useState(false);
  const [stoppingSessionId, setStoppingSessionId] = useState<string | null>(null);
  const reportsPerPage = 10;

  const isMobile = dimensions.width < 768;

  // Monitor dimension changes
  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setDimensions(window);
    });
    return () => subscription?.remove();
  }, []);

  useEffect(() => {
    fetchScrapeHistory();
    
    // Auto-refresh every 10 seconds if enabled
    let interval: NodeJS.Timeout | null = null;
    if (autoRefresh) {
      interval = setInterval(() => {
        console.log('Auto-refreshing scrape history...');
        fetchScrapeHistory();
      }, 10000); // 10 seconds
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  // Live-updating list of scrape sessions that are currently in progress. This is
  // separate from the paginated scrapeHistory fetch above — running sessions live in
  // scrapingProgress, not scrapeHistory, and only get a scrapeHistory report once
  // their batch finishes. A realtime listener (rather than the 10s poll used for
  // history) keeps this section current as channels complete.
  useEffect(() => {
    const db = getFirestore();
    const runningQuery = query(
      collection(db, 'scrapingProgress'),
      where('status', '==', 'running'),
      orderBy('startedAt', 'desc'),
      limit(10)
    );

    const unsubscribe = onSnapshot(runningQuery, (snapshot: any) => {
      const running: ScrapeReport[] = [];
      snapshot.forEach((doc: any) => {
        const data = doc.data();
        const startedAtMs = data.startedAt?.toDate ? data.startedAt.toDate().getTime() : Date.now();
        running.push({
          id: doc.id,
          sessionId: doc.id,
          type: data.type || (doc.id.startsWith('scraping_') ? 'manual' : 'scheduled'),
          success: false,
          status: 'running',
          isRunning: true,
          totalChannels: data.totalChannels || 0,
          processedChannels: data.processedChannels || 0,
          totalJobsExtracted: data.totalJobsExtracted || 0,
          totalMessagesProcessed: data.totalMessagesProcessed || 0,
          errors: data.errors || [],
          errorCount: data.errors?.length || 0,
          duration: Math.max(0, Math.round((Date.now() - startedAtMs) / 1000)),
          startedAt: data.startedAt,
          currentChannel: data.currentChannel || null,
          batchNumber: data.batchNumber || 1,
          totalBatches: data.totalBatches || 1,
          nextSessionId: data.nextSessionId || null,
          overallTotalChannels: data.overallTotalChannels || data.totalChannels || 0,
        });
      });
      setRunningReports(running);
    }, (error: any) => {
      console.error('❌ Error listening for running scrape sessions:', error);
    });

    return () => unsubscribe();
  }, []);

  const fetchScrapeHistory = async () => {
    try {
      setLoading(true);
      const db = getFirestore();
      const scrapeHistoryRef = collection(db, 'scrapeHistory');
      const q = query(scrapeHistoryRef, orderBy('createdAt', 'desc'), limit(50));
      
      // Force refresh to bypass cache - scrape history should always show latest data
      console.log('🔄 Fetching scrape history with forceRefresh=true to bypass cache...');
      const querySnapshot = await getDocs(q, { forceRefresh: true, ttl: 30000 }); // 30 second cache

      const fetchedReports: ScrapeReport[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Raw scrape history document:', { id: doc.id, data });
        
        // Map data with defaults to handle missing fields
        const report: ScrapeReport = {
          id: doc.id,
          sessionId: data.sessionId || 'N/A',
          type: data.type || 'manual',
          success: data.success ?? false,
          status: data.status || 'unknown',
          totalChannels: data.totalChannels || 0,
          processedChannels: data.processedChannels || 0,
          totalJobsExtracted: data.totalJobsExtracted || 0,
          totalMessagesProcessed: data.totalMessagesProcessed || 0,
          errors: data.errors || [],
          errorCount: data.errorCount || (data.errors?.length || 0),
          duration: data.duration || 0,
          startedAt: data.startedAt,
          completedAt: data.completedAt,
          createdAt: data.createdAt,
          digestEmailsSent: data.digestEmailsSent || 0,
          digestEmailErrors: data.digestEmailErrors || 0,
          notificationStats: data.notificationStats || null,
          batchNumber: data.batchNumber || 1,
          totalBatches: data.totalBatches || 1,
          channelDetails: data.channelDetails || [],
        };
        
        fetchedReports.push(report);
      });

      console.log(`✅ Loaded ${fetchedReports.length} scrape history records from Firestore`);
      console.log('First 3 records:', fetchedReports.slice(0, 3).map(r => ({
        id: r.id,
        sessionId: r.sessionId,
        type: r.type,
        success: r.success,
        totalJobsExtracted: r.totalJobsExtracted,
        createdAt: r.createdAt
      })));
      setReports(fetchedReports);
    } catch (error) {
      console.error('❌ Error fetching scrape history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp: Timestamp | null | undefined) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate();
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const toggleReportExpansion = (reportId: string) => {
    setExpandedReports(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reportId)) {
        newSet.delete(reportId);
      } else {
        newSet.add(reportId);
      }
      return newSet;
    });
  };

  const openReportDetail = (report: ScrapeReport) => {
    setChannelSort('jobs');
    setChannelFilterText('');
    setExpandedChannelErrors(new Set());
    setSelectedReport(report);
  };

  const toggleChannelErrorExpansion = (channelId: string) => {
    setExpandedChannelErrors(prev => {
      const newSet = new Set(prev);
      if (newSet.has(channelId)) {
        newSet.delete(channelId);
      } else {
        newSet.add(channelId);
      }
      return newSet;
    });
  };

  const handleStopScrape = async (sessionId: string) => {
    const confirmed = typeof window !== 'undefined' && window.confirm
      ? window.confirm(`Stop this scrape session?\n\nIt will finish the channel it's currently on, then stop — the rest of this batch (and any later batches in this run) will be skipped.`)
      : await new Promise<boolean>((resolve) => {
          Alert.alert(
            'Stop this scrape session?',
            "It will finish the channel it's currently on, then stop — the rest of this batch (and any later batches in this run) will be skipped.",
            [
              { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
              { text: 'Stop', style: 'destructive', onPress: () => resolve(true) },
            ]
          );
        });

    if (!confirmed) return;

    setStoppingSessionId(sessionId);
    try {
      const cancelScrapeSession = httpsCallable(functions, 'cancelScrapeSession');
      await cancelScrapeSession({ sessionId });
    } catch (error: any) {
      console.error('❌ Error stopping scrape session:', error);
      const message = error?.message || 'Failed to stop the scrape session';
      if (typeof window !== 'undefined' && window.alert) {
        window.alert(message);
      } else {
        Alert.alert('Could Not Stop Scrape', message);
      }
    } finally {
      setStoppingSessionId(null);
    }
  };

  const getStatusIcon = (success: boolean) => {
    return success ? 'check-circle' : 'error';
  };

  const getStatusColor = (success: boolean) => {
    return success ? colors.success : colors.danger;
  };

  const getTypeIcon = (type: string) => {
    return type === 'manual' ? 'pan-tool' : 'schedule';
  };

  // A cancelled report has success:false like a genuine failure, but it wasn't one —
  // an admin stopped it on purpose — so it gets its own color/label/icon rather than
  // being lumped in with "Failed" everywhere the report list renders a status badge.
  const getStatusMeta = (report: ScrapeReport) => {
    if (report.status === 'cancelled') {
      return { color: '#F59E0B', label: 'Stopped', shortLabel: 'Stopped', icon: 'stop-circle' as const };
    }
    if (report.success) {
      return { color: '#10B981', label: 'Success', shortLabel: 'OK', icon: 'check-circle' as const };
    }
    return { color: '#EF4444', label: 'Failed', shortLabel: 'Fail', icon: 'cancel' as const };
  };

  // Pagination calculations
  const totalPages = Math.ceil(reports.length / reportsPerPage);
  const startIndex = (currentPage - 1) * reportsPerPage;
  const endIndex = startIndex + reportsPerPage;
  const currentReports = reports.slice(startIndex, endIndex);

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  if (loading) {
    return (
      <View style={[styles.container, { backgroundColor: colors.lightGray }]}>
        <ActivityIndicator size="large" color={colors.beeYellow} />
        <Text style={[styles.loadingText, { color: colors.warmGray }]}>Loading scrape history...</Text>
      </View>
    );
  }

  return (
    <View style={{ flex: 1, backgroundColor: colors.adminPageBg }}>
      {/* ── Page Header ── */}
      <View style={{
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'space-between',
        backgroundColor: colors.adminHeaderBg,
        paddingHorizontal: isMobile ? 16 : 28,
        paddingVertical: isMobile ? 14 : 18,
        gap: isMobile ? 12 : 0
      }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: isMobile ? 10 : 14 }}>
          <View style={{
            width: isMobile ? 36 : 40,
            height: isMobile ? 36 : 40,
            borderRadius: 11,
            backgroundColor: `${colors.sidebarAccent}22`,
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <Icon name="history" size={isMobile ? 20 : 22} color={colors.sidebarAccent} />
          </View>
          <View>
            <Text style={{
              fontSize: isMobile ? 16 : 18,
              fontFamily: fonts.bold,
              fontWeight: '700',
              color: '#F9FAFB',
              letterSpacing: -0.3
            }}>Scrape History</Text>
            <Text style={{
              fontSize: isMobile ? 10 : 11,
              fontFamily: fonts.regular,
              color: '#64748B',
              marginTop: 1
            }}>{reports.length} total runs</Text>
          </View>
        </View>
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 10,
          width: isMobile ? '100%' : 'auto'
        }}>
          <TouchableOpacity
            onPress={() => setAutoRefresh(!autoRefresh)}
            style={{
              flex: isMobile ? 1 : undefined,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              paddingHorizontal: isMobile ? 10 : 14,
              paddingVertical: isMobile ? 7 : 8,
              borderRadius: 9,
              backgroundColor: autoRefresh ? '#10B98122' : (colors.sidebarHover),
              borderWidth: 1,
              borderColor: autoRefresh ? '#10B981' : (colors.adminBorder)
            }}
          >
            <Icon name={autoRefresh ? 'sync' : 'sync-disabled'} size={isMobile ? 14 : 15} color={autoRefresh ? '#10B981' : colors.adminMuted} />
            <Text style={{ fontSize: isMobile ? 11 : 12, fontFamily: fonts.medium, color: autoRefresh ? '#10B981' : colors.adminMuted }}>{autoRefresh ? 'Auto' : 'Manual'}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={fetchScrapeHistory}
            style={{
              flex: isMobile ? 1 : undefined,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              backgroundColor: colors.sidebarAccent,
              paddingHorizontal: isMobile ? 10 : 14,
              paddingVertical: isMobile ? 7 : 8,
              borderRadius: 9
            }}
          >
            <Icon name="refresh" size={isMobile ? 14 : 15} color="#FFFFFF" />
            <Text style={{ fontSize: isMobile ? 11 : 12, fontFamily: fonts.semiBold, fontWeight: '600', color: '#FFFFFF' }}>Refresh</Text>
          </TouchableOpacity>
        </View>
      </View>

      <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false}>
        {/* ── Currently Running ── */}
        {runningReports.length > 0 && (
          <View style={{ marginHorizontal: isMobile ? 12 : 20, marginTop: 20 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 10 }}>
              <View style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: '#10B981' }} />
              <Text style={{ fontSize: 13, fontFamily: fonts.bold, fontWeight: '700', color: colors.sidebarTextActive }}>
                Running Now ({runningReports.length})
              </Text>
            </View>
            <View style={{ gap: 10 }}>
              {runningReports.map((report) => {
                const typeColor = report.type === 'manual' ? '#F59E0B' : '#3B82F6';
                const pct = report.totalChannels > 0 ? Math.min(100, Math.round((report.processedChannels / report.totalChannels) * 100)) : 0;
                const elapsedMin = Math.floor(report.duration / 60);
                const elapsedSec = report.duration % 60;
                return (
                  <TouchableOpacity
                    key={report.id}
                    onPress={() => openReportDetail(report)}
                    activeOpacity={0.85}
                    style={{
                      backgroundColor: colors.adminCardBg, borderRadius: 14, padding: isMobile ? 14 : 16,
                      borderLeftWidth: 4, borderLeftColor: '#10B981',
                      ...Platform.select({ web: { boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.06)' }, default: { shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.07, shadowRadius: 6, elevation: 2 } }),
                    }}
                  >
                    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexShrink: 1 }}>
                        <Text style={{ fontSize: 12, fontFamily: fonts.medium, fontWeight: '600', color: colors.sidebarTextActive }} numberOfLines={1}>
                          {report.currentChannel ? `Processing ${report.currentChannel}` : 'Starting up...'}
                        </Text>
                        {(report.totalBatches || 1) > 1 && (
                          <View style={{ paddingHorizontal: 7, paddingVertical: 2, borderRadius: 20, backgroundColor: colors.sidebarHover }}>
                            <Text style={{ fontSize: 9, fontWeight: '600', color: colors.adminMuted }}>Batch {report.batchNumber}/{report.totalBatches}</Text>
                          </View>
                        )}
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 7, paddingVertical: 2, borderRadius: 20, backgroundColor: `${typeColor}20` }}>
                          <Icon name={report.type === 'manual' ? 'pan-tool' : 'schedule'} size={9} color={typeColor} />
                          <Text style={{ fontSize: 9, fontWeight: '600', color: typeColor, textTransform: 'capitalize' }}>{report.type}</Text>
                        </View>
                      </View>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                        <Text style={{ fontSize: 10, fontFamily: fonts.regular, color: colors.adminMuted }}>{elapsedMin}m {elapsedSec}s elapsed</Text>
                        <TouchableOpacity
                          onPress={(e) => { e.stopPropagation?.(); handleStopScrape(report.sessionId); }}
                          disabled={stoppingSessionId === report.sessionId}
                          style={{ flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 20, backgroundColor: '#EF444420', opacity: stoppingSessionId === report.sessionId ? 0.6 : 1 }}
                        >
                          {stoppingSessionId === report.sessionId ? (
                            <ActivityIndicator size="small" color="#EF4444" />
                          ) : (
                            <>
                              <Icon name="stop-circle" size={11} color="#EF4444" />
                              <Text style={{ fontSize: 10, fontWeight: '600', color: '#EF4444' }}>Stop</Text>
                            </>
                          )}
                        </TouchableOpacity>
                      </View>
                    </View>

                    <View style={{ height: 6, borderRadius: 3, backgroundColor: colors.sidebarHover, overflow: 'hidden', marginBottom: 8 }}>
                      <View style={{ height: '100%', width: `${pct}%` as any, borderRadius: 3, backgroundColor: '#10B981' }} />
                    </View>

                    <View style={{ flexDirection: 'row', gap: 16 }}>
                      <Text style={{ fontSize: 11, fontFamily: fonts.regular, color: colors.adminMuted }}>
                        {report.processedChannels}/{report.totalChannels} channels ({pct}%)
                      </Text>
                      <Text style={{ fontSize: 11, fontFamily: fonts.regular, color: colors.adminMuted }}>
                        {report.totalJobsExtracted} jobs
                      </Text>
                      <Text style={{ fontSize: 11, fontFamily: fonts.regular, color: colors.adminMuted }}>
                        {report.totalMessagesProcessed} messages
                      </Text>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        )}

        {/* ── Stats Grid ── */}
        <View style={{
          flexDirection: isMobile ? 'column' : 'row',
          gap: 12,
          paddingHorizontal: isMobile ? 16 : 20,
          paddingTop: 20,
          paddingBottom: 14,
          flexWrap: 'wrap'
        }}>
          {[
            { label: 'Total Runs', value: reports.length, color: '#8B5CF6', icon: 'assessment' as const },
            { label: 'Successful', value: reports.filter(r => r.success).length, color: '#10B981', icon: 'check-circle' as const },
            { label: 'Failed', value: reports.filter(r => !r.success && r.status !== 'cancelled').length, color: '#EF4444', icon: 'cancel' as const },
            { label: 'Jobs Extracted', value: reports.reduce((sum, r) => sum + r.totalJobsExtracted, 0), color: '#F59E0B', icon: 'work' as const }
          ].map((stat, idx) => (
            <View key={idx} style={{ flex: 1, minWidth: 220, backgroundColor: colors.adminCardBg, borderTopWidth: 3, borderTopColor: stat.color, borderRadius: 12, padding: 14, ...Platform.select({ web: { boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }, default: { shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 4, elevation: 2 } }) }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <View>
                  <Text style={{ fontSize: 11, fontFamily: fonts.medium, color: colors.adminMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{stat.label}</Text>
                  <Text style={{ fontSize: 24, fontFamily: fonts.bold, fontWeight: '700', color: colors.sidebarTextActive, marginTop: 4 }}>{stat.value}</Text>
                </View>
                <View style={{ width: 36, height: 36, borderRadius: 9, backgroundColor: `${stat.color}15`, justifyContent: 'center', alignItems: 'center' }}>
                  <Icon name={stat.icon} size={18} color={stat.color} />
                </View>
              </View>
            </View>
          ))}
        </View>

        {/* ── History List ── */}
        <View style={{ marginHorizontal: isMobile ? 12 : 20, marginBottom: 20 }}>
          {reports.length === 0 ? (
            <View style={{
              backgroundColor: colors.adminCardBg,
              borderRadius: 14, padding: 32, alignItems: 'center',
              ...Platform.select({ web: { boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }, default: { shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.06, shadowRadius: 6, elevation: 2 } }),
            }}>
              <Icon name="inbox" size={48} color={colors.adminMuted} />
              <Text style={{ fontSize: 14, color: colors.adminMuted, marginTop: 12, fontFamily: fonts.regular }}>No scrape history found</Text>
            </View>
          ) : isMobile ? (
            /* ── Mobile: card list ── */
            <View style={{ gap: 10 }}>
              {currentReports.map((report) => {
                const statusMeta = getStatusMeta(report);
                const statusColor = statusMeta.color;
                const typeColor = report.type === 'manual' ? '#F59E0B' : '#3B82F6';
                return (
                  <TouchableOpacity
                    key={report.id}
                    onPress={() => openReportDetail(report)}
                    activeOpacity={0.85}
                    style={{
                      backgroundColor: colors.adminCardBg,
                      borderRadius: 14,
                      borderLeftWidth: 4,
                      borderLeftColor: statusColor,
                      overflow: 'hidden',
                      ...Platform.select({ web: { boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.06)' }, default: { shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.07, shadowRadius: 6, elevation: 2 } }),
                    }}
                  >
                    {/* Card top row */}
                    <View style={{ flexDirection: 'row', alignItems: 'center', padding: 14, gap: 10 }}>
                      {/* Status icon */}
                      <View style={{ width: 38, height: 38, borderRadius: 10, backgroundColor: `${statusColor}18`, justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
                        <Icon name={statusMeta.icon} size={20} color={statusColor} />
                      </View>

                      {/* Date + session */}
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <Text style={{ fontSize: 13, fontWeight: '600', color: colors.sidebarTextActive, marginBottom: 2 }} numberOfLines={1}>
                          {formatDate(report.completedAt)}
                        </Text>
                        <Text style={{ fontSize: 10, color: colors.adminMuted }} numberOfLines={1}>
                          {report.sessionId}
                        </Text>
                      </View>

                      {/* Type + batch + status badges */}
                      <View style={{ flexDirection: 'row', gap: 6, flexShrink: 0 }}>
                        {(report.totalBatches || 1) > 1 && (
                          <View style={{ justifyContent: 'center', paddingHorizontal: 7, paddingVertical: 3, borderRadius: 20, backgroundColor: colors.sidebarHover }}>
                            <Text style={{ fontSize: 10, fontWeight: '600', color: colors.adminMuted }}>Batch {report.batchNumber}/{report.totalBatches}</Text>
                          </View>
                        )}
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 7, paddingVertical: 3, borderRadius: 20, backgroundColor: `${typeColor}20` }}>
                          <Icon name={report.type === 'manual' ? 'pan-tool' : 'schedule'} size={10} color={typeColor} />
                          <Text style={{ fontSize: 10, fontWeight: '600', color: typeColor, textTransform: 'capitalize' }}>{report.type}</Text>
                        </View>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 7, paddingVertical: 3, borderRadius: 20, backgroundColor: `${statusColor}20` }}>
                          <Text style={{ fontSize: 10, fontWeight: '600', color: statusColor }}>{statusMeta.shortLabel}</Text>
                        </View>
                      </View>
                    </View>

                    {/* Stats row */}
                    <View style={{ flexDirection: 'row', paddingHorizontal: 14, paddingBottom: 14, gap: 8 }}>
                      {[
                        { label: 'Channels', value: `${report.processedChannels}/${report.totalChannels}`, color: '#3B82F6' },
                        { label: 'Jobs', value: String(report.totalJobsExtracted), color: '#8B5CF6' },
                        { label: 'Messages', value: String(report.totalMessagesProcessed), color: '#10B981' },
                        { label: 'Duration', value: formatDuration(report.duration), color: '#F59E0B' },
                      ].map((s) => (
                        <View key={s.label} style={{ flex: 1, backgroundColor: colors.sidebarHover, borderRadius: 8, paddingVertical: 6, paddingHorizontal: 4, alignItems: 'center' }}>
                          <Text style={{ fontSize: 12, fontWeight: '700', color: s.color }}>{s.value}</Text>
                          <Text style={{ fontSize: 9, color: colors.adminMuted, marginTop: 1 }}>{s.label}</Text>
                        </View>
                      ))}
                    </View>

                    {/* Error indicator */}
                    {report.errorCount > 0 && (
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 14, paddingBottom: 12 }}>
                        <Icon name="warning" size={13} color="#EF4444" />
                        <Text style={{ fontSize: 11, color: '#EF4444', fontWeight: '600' }}>{report.errorCount} error{report.errorCount !== 1 ? 's' : ''}</Text>
                      </View>
                    )}
                  </TouchableOpacity>
                );
              })}
            </View>
          ) : (
            /* ── Desktop: table ── */
            <View style={{ borderRadius: 14, overflow: 'hidden', backgroundColor: colors.adminCardBg, ...Platform.select({ web: { boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }, default: { shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.06, shadowRadius: 6, elevation: 3 } }) }}>
              {/* Table Header */}
              <View style={{ flexDirection: 'row', backgroundColor: '#1E293B', paddingVertical: 11, paddingHorizontal: 14, gap: 10 }}>
                {['Status', 'Type', 'Date', 'Channels', 'Jobs', 'Messages', 'Duration', 'Actions'].map((label, i) => (
                  <Text key={i} style={{ flex: i === 2 ? 1.3 : i === 7 ? 0.8 : 1, fontSize: 11, fontFamily: fonts.bold, fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</Text>
                ))}
              </View>
              {currentReports.map((report, idx) => {
                const statusMeta = getStatusMeta(report);
                const statusColor = statusMeta.color;
                const typeColor = report.type === 'manual' ? '#F59E0B' : '#3B82F6';
                return (
                  <View key={report.id} style={{ flexDirection: 'row', paddingVertical: 13, paddingHorizontal: 14, gap: 10, alignItems: 'center', backgroundColor: isDarkMode ? (idx % 2 === 0 ? '#111827' : '#161F2E') : (idx % 2 === 0 ? '#F9FAFB' : '#FFFFFF'), borderBottomWidth: idx === currentReports.length - 1 ? 0 : 1, borderBottomColor: colors.adminBorder }}>
                    <View style={{ flex: 1 }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 20, backgroundColor: `${statusColor}20`, alignSelf: 'flex-start' }}>
                        <Icon name={statusMeta.icon} size={13} color={statusColor} />
                        <Text style={{ fontSize: 11, fontFamily: fonts.medium, color: statusColor }}>{statusMeta.label}</Text>
                      </View>
                    </View>
                    <View style={{ flex: 1 }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 20, backgroundColor: `${typeColor}20`, alignSelf: 'flex-start' }}>
                        <Icon name={report.type === 'manual' ? 'pan-tool' : 'schedule'} size={12} color={typeColor} />
                        <Text style={{ fontSize: 11, fontFamily: fonts.medium, color: typeColor, textTransform: 'capitalize' }}>{report.type}</Text>
                      </View>
                      {(report.totalBatches || 1) > 1 && (
                        <Text style={{ fontSize: 10, color: colors.adminMuted, marginTop: 3 }}>Batch {report.batchNumber}/{report.totalBatches}</Text>
                      )}
                    </View>
                    <Text style={{ flex: 1.3, fontSize: 12, fontFamily: fonts.regular, color: colors.adminMuted }}>{formatDate(report.completedAt)}</Text>
                    <Text style={{ flex: 1, fontSize: 12, fontFamily: fonts.medium, color: colors.sidebarText }}>{report.processedChannels}/{report.totalChannels}</Text>
                    <Text style={{ flex: 1, fontSize: 12, fontFamily: fonts.bold, fontWeight: '700', color: '#8B5CF6' }}>{report.totalJobsExtracted}</Text>
                    <Text style={{ flex: 1, fontSize: 12, fontFamily: fonts.regular, color: colors.adminMuted }}>{report.totalMessagesProcessed}</Text>
                    <Text style={{ flex: 1, fontSize: 12, fontFamily: fonts.regular, color: colors.adminMuted }}>{formatDuration(report.duration)}</Text>
                    <View style={{ flex: 0.8 }}>
                      <TouchableOpacity onPress={() => openReportDetail(report)} style={{ paddingHorizontal: 10, paddingVertical: 5, backgroundColor: '#8B5CF622', borderRadius: 7 }}>
                        <Text style={{ fontSize: 11, fontFamily: fonts.semiBold, fontWeight: '600', color: '#8B5CF6' }}>View</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                );
              })}
            </View>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <View style={{
              flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
              paddingHorizontal: isMobile ? 4 : 14, paddingVertical: 12, marginTop: 10,
              backgroundColor: colors.adminCardBg,
              borderRadius: 12,
              ...Platform.select({ web: { boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }, default: { shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 4, elevation: 1 } }),
            }}>
              <TouchableOpacity
                onPress={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                style={{ paddingHorizontal: isMobile ? 14 : 12, paddingVertical: 8, borderRadius: 20, backgroundColor: currentPage === 1 ? (isDarkMode ? '#1E293B' : '#E2E8F0') : '#8B5CF6' }}
              >
                <Text style={{ fontSize: 12, fontFamily: fonts.medium, color: currentPage === 1 ? '#64748B' : '#FFFFFF' }}>← Prev</Text>
              </TouchableOpacity>
              <Text style={{ fontSize: isMobile ? 11 : 12, fontFamily: fonts.regular, color: colors.adminMuted, textAlign: 'center' }}>
                {isMobile ? `${currentPage}/${totalPages}` : `Page ${currentPage} of ${totalPages} · ${reports.length} records`}
              </Text>
              <TouchableOpacity
                onPress={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{ paddingHorizontal: isMobile ? 14 : 12, paddingVertical: 8, borderRadius: 20, backgroundColor: currentPage === totalPages ? (isDarkMode ? '#1E293B' : '#E2E8F0') : '#8B5CF6' }}
              >
                <Text style={{ fontSize: 12, fontFamily: fonts.medium, color: currentPage === totalPages ? '#64748B' : '#FFFFFF' }}>Next →</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </ScrollView>

      {/* ── Detail Modal ── */}
      {selectedReport && (
        <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: isMobile ? 'flex-end' : 'center', alignItems: isMobile ? 'stretch' : 'center', zIndex: 1000 }}>
          <View style={{
            width: isMobile ? '100%' : '90%',
            maxWidth: isMobile ? undefined : 740,
            maxHeight: isMobile ? '92%' : '90%',
            backgroundColor: colors.adminCardAltBg,
            borderRadius: isMobile ? 20 : 16,
            borderBottomLeftRadius: isMobile ? 0 : undefined,
            borderBottomRightRadius: isMobile ? 0 : undefined,
            overflow: 'hidden',
            ...Platform.select({ web: { boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }, default: { shadowColor: '#000', shadowOffset: { width: 0, height: -4 }, shadowOpacity: 0.15, shadowRadius: 20, elevation: 10 } }),
          }}>
            {/* Drag handle on mobile */}
            {isMobile && (
              <View style={{ alignItems: 'center', paddingTop: 10, paddingBottom: 4 }}>
                <View style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: colors.adminBorder }} />
              </View>
            )}

            {/* Modal Header */}
            <View style={{ backgroundColor: '#1E293B', paddingHorizontal: isMobile ? 18 : 24, paddingVertical: isMobile ? 14 : 16, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                <View style={{ width: 36, height: 36, borderRadius: 9, backgroundColor: selectedReport.isRunning ? '#10B98122' : '#8B5CF622', justifyContent: 'center', alignItems: 'center' }}>
                  {selectedReport.isRunning ? <ActivityIndicator size="small" color="#10B981" /> : <Icon name="assessment" size={18} color="#8B5CF6" />}
                </View>
                <View>
                  <Text style={{ fontSize: isMobile ? 14 : 16, fontFamily: fonts.bold, fontWeight: '700', color: '#F9FAFB' }}>
                    {selectedReport.isRunning ? 'Live Scrape Session' : 'Scrape Session Report'}{(selectedReport.totalBatches || 1) > 1 ? ` — Batch ${selectedReport.batchNumber}/${selectedReport.totalBatches}` : ''}
                  </Text>
                  <TouchableOpacity
                    onPress={() => {
                      Clipboard.setString(selectedReport.sessionId);
                      setCopiedSessionId(true);
                      setTimeout(() => setCopiedSessionId(false), 1500);
                    }}
                    style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 1 }}
                  >
                    <Text style={{ fontSize: 10, fontFamily: fonts.regular, color: '#64748B' }} numberOfLines={1}>{selectedReport.sessionId}</Text>
                    <Icon name={copiedSessionId ? 'check' : 'content-copy'} size={11} color={copiedSessionId ? '#10B981' : '#64748B'} />
                  </TouchableOpacity>
                </View>
              </View>
              <TouchableOpacity onPress={() => setSelectedReport(null)} style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: '#FFFFFF18', justifyContent: 'center', alignItems: 'center' }}>
                <Icon name="close" size={18} color="#94A3B8" />
              </TouchableOpacity>
            </View>

            {/* Modal Body */}
            <ScrollView style={{ maxHeight: isMobile ? undefined : 600 }} showsVerticalScrollIndicator={false}>
              <View style={{ padding: isMobile ? 16 : 24 }}>
                {/* Status Banner */}
                {(() => {
                  const bannerMeta = selectedReport.isRunning
                    ? { color: '#10B981', label: 'In Progress', icon: 'check' as const }
                    : getStatusMeta(selectedReport);
                  return (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderRadius: 10, backgroundColor: `${bannerMeta.color}15`, borderWidth: 1, borderColor: `${bannerMeta.color}30`, marginBottom: 16 }}>
                  <View style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: bannerMeta.color, justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
                    {selectedReport.isRunning ? (
                      <ActivityIndicator size="small" color="#FFFFFF" />
                    ) : (
                      <Icon name={bannerMeta.icon} size={20} color="#FFFFFF" />
                    )}
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: isMobile ? 13 : 14, fontFamily: fonts.bold, fontWeight: '700', color: bannerMeta.color }}>
                      {selectedReport.isRunning ? 'In Progress' : (selectedReport.status === 'cancelled' ? 'Stopped by Admin' : (selectedReport.success ? 'Completed Successfully' : 'Scrape Failed'))}
                    </Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 2, borderRadius: 20, backgroundColor: selectedReport.type === 'manual' ? '#F59E0B20' : '#3B82F620' }}>
                        <Icon name={selectedReport.type === 'manual' ? 'pan-tool' : 'schedule'} size={10} color={selectedReport.type === 'manual' ? '#F59E0B' : '#3B82F6'} />
                        <Text style={{ fontSize: 10, fontFamily: fonts.medium, color: selectedReport.type === 'manual' ? '#F59E0B' : '#3B82F6' }}>{selectedReport.type === 'manual' ? 'Manual' : 'Scheduled'}</Text>
                      </View>
                      <Text style={{ fontSize: 11, fontFamily: fonts.regular, color: colors.adminMuted }}>
                        {selectedReport.isRunning
                          ? (selectedReport.currentChannel ? `Processing ${selectedReport.currentChannel}` : 'Starting up...')
                          : formatDate(selectedReport.completedAt)}
                      </Text>
                    </View>
                  </View>
                  {selectedReport.isRunning && (
                    <TouchableOpacity
                      onPress={() => handleStopScrape(selectedReport.sessionId)}
                      disabled={stoppingSessionId === selectedReport.sessionId}
                      style={{ flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8, backgroundColor: '#EF4444', opacity: stoppingSessionId === selectedReport.sessionId ? 0.6 : 1, flexShrink: 0 }}
                    >
                      {stoppingSessionId === selectedReport.sessionId ? (
                        <ActivityIndicator size="small" color="#FFFFFF" />
                      ) : (
                        <>
                          <Icon name="stop-circle" size={14} color="#FFFFFF" />
                          <Text style={{ fontSize: 11, fontFamily: fonts.semiBold, fontWeight: '600', color: '#FFFFFF' }}>Stop</Text>
                        </>
                      )}
                    </TouchableOpacity>
                  )}
                </View>
                  );
                })()}

                {/* Metrics Grid */}
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: isMobile ? 8 : 12, marginBottom: 16 }}>
                  {[
                    { label: 'Duration', value: formatDuration(selectedReport.duration), color: '#F59E0B', icon: 'timer' as const },
                    { label: 'Channels', value: `${selectedReport.processedChannels}/${selectedReport.totalChannels}`, color: '#3B82F6', icon: 'podcasts' as const },
                    { label: 'Jobs Found', value: selectedReport.totalJobsExtracted, color: '#8B5CF6', icon: 'work' as const },
                    { label: 'Messages', value: selectedReport.totalMessagesProcessed, color: '#10B981', icon: 'message' as const },
                    {
                      label: 'Conversion',
                      value: selectedReport.totalMessagesProcessed > 0
                        ? `${((selectedReport.totalJobsExtracted / selectedReport.totalMessagesProcessed) * 100).toFixed(1)}%`
                        : 'N/A',
                      color: '#EC4899', icon: 'insights' as const
                    }
                  ].map((metric, idx) => (
                    <View key={idx} style={{ flex: 1, minWidth: isMobile ? 130 : 150, backgroundColor: colors.adminCardBg, borderRadius: 10, padding: isMobile ? 10 : 12, borderLeftWidth: 3, borderLeftColor: metric.color }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                        <Icon name={metric.icon} size={14} color={metric.color} />
                        <Text style={{ fontSize: 10, fontFamily: fonts.medium, color: colors.adminMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{metric.label}</Text>
                      </View>
                      <Text style={{ fontSize: isMobile ? 16 : 18, fontFamily: fonts.bold, fontWeight: '700', color: colors.sidebarTextActive }}>{metric.value}</Text>
                    </View>
                  ))}
                </View>

                {/* Notifications */}
                {(selectedReport.notificationStats || (selectedReport.digestEmailsSent || 0) > 0 || (selectedReport.digestEmailErrors || 0) > 0) && (
                  <View style={{ backgroundColor: colors.adminCardBg, borderRadius: 10, padding: isMobile ? 14 : 16, marginBottom: 16 }}>
                    <Text style={{ fontSize: 13, fontFamily: fonts.bold, fontWeight: '700', color: colors.sidebarTextActive, marginBottom: 12 }}>Notifications</Text>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: isMobile ? 8 : 12, marginBottom: (selectedReport.notificationStats?.byCategory ? 12 : 0) }}>
                      {[
                        { label: 'Users Matched', value: selectedReport.notificationStats?.totalUsersMatched || 0, color: '#F59E0B', icon: 'how-to-reg' as const },
                        { label: 'Emails Sent', value: selectedReport.digestEmailsSent || 0, color: '#3B82F6', icon: 'mail-outline' as const },
                        { label: 'Push Sent', value: selectedReport.notificationStats?.pushNotificationsSent || 0, color: '#10B981', icon: 'notifications-none' as const },
                      ].map((metric, idx) => (
                        <View key={idx} style={{ flex: 1, minWidth: isMobile ? 100 : 130, backgroundColor: colors.adminCardAltBg, borderRadius: 10, padding: isMobile ? 10 : 12, borderLeftWidth: 3, borderLeftColor: metric.color }}>
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                            <Icon name={metric.icon} size={14} color={metric.color} />
                            <Text style={{ fontSize: 10, fontFamily: fonts.medium, color: colors.adminMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{metric.label}</Text>
                          </View>
                          <Text style={{ fontSize: isMobile ? 16 : 18, fontFamily: fonts.bold, fontWeight: '700', color: colors.sidebarTextActive }}>{metric.value}</Text>
                        </View>
                      ))}
                    </View>

                    {(selectedReport.digestEmailErrors || 0) > 0 && (
                      <Text style={{ fontSize: 11, fontFamily: fonts.medium, color: '#EF4444', marginBottom: selectedReport.notificationStats?.byCategory ? 8 : 0 }}>
                        {selectedReport.digestEmailErrors} digest email{selectedReport.digestEmailErrors === 1 ? '' : 's'} failed to send
                      </Text>
                    )}

                    {selectedReport.notificationStats?.byCategory && Object.keys(selectedReport.notificationStats.byCategory).length > 0 && (
                      <View>
                        <Text style={{ fontSize: 10, fontFamily: fonts.medium, color: colors.adminMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Matches by job type</Text>
                        {Object.entries(selectedReport.notificationStats.byCategory)
                          .sort(([, a], [, b]) => b.usersMatched - a.usersMatched)
                          .map(([category, catStats]) => (
                            <View key={category} style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: colors.adminBorder }}>
                              <Text style={{ fontSize: 12, fontFamily: fonts.medium, color: colors.sidebarText, flex: 1 }} numberOfLines={1}>{category}</Text>
                              <Text style={{ fontSize: 11, fontFamily: fonts.regular, color: colors.adminMuted }}>{catStats.jobs} job{catStats.jobs === 1 ? '' : 's'} • {catStats.usersMatched} matched</Text>
                            </View>
                          ))}
                      </View>
                    )}
                  </View>
                )}

                {/* Channel Breakdown */}
                <View style={{ backgroundColor: colors.adminCardBg, borderRadius: 10, padding: isMobile ? 14 : 16, marginBottom: 16 }}>
                  {!selectedReport.channelDetails || selectedReport.channelDetails.length === 0 ? (
                    <>
                      <Text style={{ fontSize: 13, fontFamily: fonts.bold, fontWeight: '700', color: colors.sidebarTextActive, marginBottom: 6 }}>Channel Breakdown</Text>
                      <Text style={{ fontSize: 12, fontFamily: fonts.regular, color: colors.adminMuted }}>
                        {selectedReport.isRunning ? 'Per-channel results will appear here as this batch finishes.' : "Per-channel detail isn't available for this report."}
                      </Text>
                    </>
                  ) : (() => {
                    const channels = selectedReport.channelDetails!;
                    const withJobs = channels.filter(c => c.jobsExtracted > 0).length;
                    const withErrors = channels.filter(c => c.errors && c.errors.length > 0).length;
                    const filtered = channels.filter(c => {
                      const q = channelFilterText.trim().toLowerCase();
                      if (!q) return true;
                      return c.channelName?.toLowerCase().includes(q) || c.channelUsername?.toLowerCase().includes(q);
                    });
                    const sorted = [...filtered].sort((a, b) => {
                      if (channelSort === 'jobs') return b.jobsExtracted - a.jobsExtracted;
                      if (channelSort === 'errors') return (b.errors?.length || 0) - (a.errors?.length || 0);
                      return (a.channelName || a.channelUsername || '').localeCompare(b.channelName || b.channelUsername || '');
                    });

                    return (
                      <>
                        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
                          <Text style={{ fontSize: 13, fontFamily: fonts.bold, fontWeight: '700', color: colors.sidebarTextActive }}>
                            Channel Breakdown ({channels.length})
                          </Text>
                          <View style={{ flexDirection: 'row', gap: 6 }}>
                            {([
                              { key: 'jobs' as const, label: 'Jobs' },
                              { key: 'errors' as const, label: 'Errors' },
                              { key: 'alpha' as const, label: 'A–Z' },
                            ]).map(opt => (
                              <TouchableOpacity
                                key={opt.key}
                                onPress={() => setChannelSort(opt.key)}
                                style={{
                                  paddingHorizontal: 9, paddingVertical: 4, borderRadius: 20,
                                  backgroundColor: channelSort === opt.key ? '#8B5CF6' : colors.sidebarHover,
                                }}
                              >
                                <Text style={{ fontSize: 10, fontFamily: fonts.medium, fontWeight: '600', color: channelSort === opt.key ? '#FFFFFF' : colors.adminMuted }}>{opt.label}</Text>
                              </TouchableOpacity>
                            ))}
                          </View>
                        </View>

                        {/* Rollup chips */}
                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 20, backgroundColor: '#10B98120' }}>
                            <Icon name="check-circle" size={11} color="#10B981" />
                            <Text style={{ fontSize: 10, fontFamily: fonts.medium, color: '#10B981' }}>{withJobs} yielded jobs</Text>
                          </View>
                          {withErrors > 0 && (
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 20, backgroundColor: '#EF444420' }}>
                              <Icon name="error-outline" size={11} color="#EF4444" />
                              <Text style={{ fontSize: 10, fontFamily: fonts.medium, color: '#EF4444' }}>{withErrors} had errors</Text>
                            </View>
                          )}
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 20, backgroundColor: colors.sidebarHover }}>
                            <Icon name="hourglass-empty" size={11} color={colors.adminMuted} />
                            <Text style={{ fontSize: 10, fontFamily: fonts.medium, color: colors.adminMuted }}>{channels.length - withJobs} no jobs found</Text>
                          </View>
                        </View>

                        {/* Filter box (helpful once a report has many channels) */}
                        {channels.length > 6 && (
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: colors.adminCardAltBg, borderRadius: 8, paddingHorizontal: 10, paddingVertical: Platform.OS === 'web' ? 6 : 2, marginBottom: 10 }}>
                            <Icon name="search" size={14} color={colors.adminMuted} />
                            <TextInput
                              value={channelFilterText}
                              onChangeText={setChannelFilterText}
                              placeholder="Filter by channel name..."
                              placeholderTextColor={colors.adminMuted}
                              style={{ flex: 1, fontSize: 12, color: colors.sidebarTextActive, paddingVertical: 4 }}
                            />
                          </View>
                        )}

                        {sorted.length === 0 ? (
                          <Text style={{ fontSize: 12, fontFamily: fonts.regular, color: colors.adminMuted }}>No channels match "{channelFilterText}"</Text>
                        ) : (
                          <View style={{ gap: 8 }}>
                            {sorted.map((channel) => {
                              const hasErrors = channel.errors && channel.errors.length > 0;
                              const isExpanded = expandedChannelErrors.has(channel.channelId);
                              const conversion = channel.messagesProcessed > 0
                                ? ((channel.jobsExtracted / channel.messagesProcessed) * 100).toFixed(0)
                                : null;
                              return (
                                <View
                                  key={channel.channelId}
                                  style={{
                                    backgroundColor: colors.adminCardAltBg, borderRadius: 8, padding: 10,
                                    borderLeftWidth: 3, borderLeftColor: hasErrors ? '#EF4444' : (channel.jobsExtracted > 0 ? '#10B981' : colors.adminBorder),
                                  }}
                                >
                                  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                    <View style={{ flex: 1, minWidth: 0 }}>
                                      <Text style={{ fontSize: 12, fontFamily: fonts.medium, fontWeight: '600', color: colors.sidebarTextActive }} numberOfLines={1}>
                                        {channel.channelName || channel.channelUsername}
                                      </Text>
                                      <Text style={{ fontSize: 10, fontFamily: fonts.regular, color: colors.adminMuted }} numberOfLines={1}>
                                        @{channel.channelUsername}
                                      </Text>
                                    </View>
                                    <View style={{ flexDirection: 'row', gap: 10, flexShrink: 0 }}>
                                      <View style={{ alignItems: 'center' }}>
                                        <Text style={{ fontSize: 12, fontFamily: fonts.bold, fontWeight: '700', color: colors.sidebarTextActive }}>{channel.messagesProcessed}</Text>
                                        <Text style={{ fontSize: 9, color: colors.adminMuted }}>msgs</Text>
                                      </View>
                                      <View style={{ alignItems: 'center' }}>
                                        <Text style={{ fontSize: 12, fontFamily: fonts.bold, fontWeight: '700', color: channel.jobsExtracted > 0 ? '#10B981' : colors.adminMuted }}>{channel.jobsExtracted}</Text>
                                        <Text style={{ fontSize: 9, color: colors.adminMuted }}>jobs</Text>
                                      </View>
                                      {conversion !== null && (
                                        <View style={{ alignItems: 'center' }}>
                                          <Text style={{ fontSize: 12, fontFamily: fonts.bold, fontWeight: '700', color: colors.sidebarTextActive }}>{conversion}%</Text>
                                          <Text style={{ fontSize: 9, color: colors.adminMuted }}>rate</Text>
                                        </View>
                                      )}
                                    </View>
                                  </View>

                                  {hasErrors && (
                                    <TouchableOpacity onPress={() => toggleChannelErrorExpansion(channel.channelId)} style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 8 }}>
                                      <Icon name={isExpanded ? 'expand-less' : 'expand-more'} size={13} color="#EF4444" />
                                      <Text style={{ fontSize: 10, fontFamily: fonts.medium, color: '#EF4444' }}>
                                        {channel.errors.length} error{channel.errors.length === 1 ? '' : 's'} — {isExpanded ? 'hide' : 'view'}
                                      </Text>
                                    </TouchableOpacity>
                                  )}
                                  {hasErrors && isExpanded && (
                                    <View style={{ marginTop: 6, gap: 4 }}>
                                      {channel.errors.map((err, i) => (
                                        <Text key={i} selectable style={{ fontSize: 10, fontFamily: fonts.regular, color: colors.adminMuted, backgroundColor: '#EF444410', borderRadius: 6, padding: 6 }}>{err}</Text>
                                      ))}
                                    </View>
                                  )}
                                </View>
                              );
                            })}
                          </View>
                        )}
                      </>
                    );
                  })()}
                </View>

                {/* Timeline */}
                {selectedReport.startedAt && (selectedReport.completedAt || selectedReport.isRunning) && (
                  <View style={{ backgroundColor: colors.adminCardBg, borderRadius: 10, padding: isMobile ? 14 : 16, marginBottom: 16 }}>
                    <Text style={{ fontSize: 13, fontFamily: fonts.bold, fontWeight: '700', color: colors.sidebarTextActive, marginBottom: 12 }}>Execution Timeline</Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: selectedReport.completedAt ? 8 : 0 }}>
                      <View style={{ width: 24, height: 24, borderRadius: 12, backgroundColor: '#10B981', justifyContent: 'center', alignItems: 'center' }}>
                        <Icon name="play-arrow" size={14} color="#FFFFFF" />
                      </View>
                      <View>
                        <Text style={{ fontSize: 10, fontFamily: fonts.medium, color: colors.adminMuted, textTransform: 'uppercase' }}>Started</Text>
                        <Text style={{ fontSize: 12, fontFamily: fonts.regular, color: colors.sidebarText }}>{formatDate(selectedReport.startedAt)}</Text>
                      </View>
                    </View>
                    {selectedReport.completedAt ? (
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                        <View style={{ width: 24, height: 24, borderRadius: 12, backgroundColor: getStatusMeta(selectedReport).color, justifyContent: 'center', alignItems: 'center' }}>
                          <Icon name={selectedReport.status === 'cancelled' ? 'stop' : (selectedReport.success ? 'check' : 'close')} size={14} color="#FFFFFF" />
                        </View>
                        <View>
                          <Text style={{ fontSize: 10, fontFamily: fonts.medium, color: colors.adminMuted, textTransform: 'uppercase' }}>{selectedReport.status === 'cancelled' ? 'Stopped' : 'Completed'}</Text>
                          <Text style={{ fontSize: 12, fontFamily: fonts.regular, color: colors.sidebarText }}>{formatDate(selectedReport.completedAt)}</Text>
                        </View>
                      </View>
                    ) : (
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 8 }}>
                        <View style={{ width: 24, height: 24, borderRadius: 12, backgroundColor: '#10B98130', justifyContent: 'center', alignItems: 'center' }}>
                          <ActivityIndicator size="small" color="#10B981" />
                        </View>
                        <View>
                          <Text style={{ fontSize: 10, fontFamily: fonts.medium, color: colors.adminMuted, textTransform: 'uppercase' }}>Still running</Text>
                          <Text style={{ fontSize: 12, fontFamily: fonts.regular, color: colors.sidebarText }}>{Math.floor(selectedReport.duration / 60)}m {selectedReport.duration % 60}s elapsed so far</Text>
                        </View>
                      </View>
                    )}
                  </View>
                )}

                {/* Errors */}
                {selectedReport.errors && selectedReport.errors.length > 0 && (
                  <View style={{ backgroundColor: colors.adminCardBg, borderRadius: 10, padding: isMobile ? 14 : 16, borderLeftWidth: 3, borderLeftColor: '#EF4444' }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                      <Icon name="warning" size={16} color="#EF4444" />
                      <Text style={{ fontSize: 13, fontFamily: fonts.bold, fontWeight: '700', color: '#EF4444' }}>Errors ({selectedReport.errorCount})</Text>
                    </View>
                    {selectedReport.errors.map((error, index) => (
                      <View key={index} style={{ backgroundColor: '#EF444410', borderRadius: 6, padding: 10, marginBottom: 8 }}>
                        <Text style={{ fontSize: 10, fontFamily: fonts.medium, color: colors.adminMuted, marginBottom: 2 }}>Error #{index + 1}</Text>
                        <Text style={{ fontSize: 11, fontFamily: fonts.regular, color: colors.sidebarText }} selectable>{error}</Text>
                      </View>
                    ))}
                  </View>
                )}

                {/* Success Footer */}
                {selectedReport.success && selectedReport.totalJobsExtracted > 0 && (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, padding: 14, borderRadius: 10, backgroundColor: '#10B98115', borderWidth: 1, borderColor: '#10B98130', marginTop: 16 }}>
                    <Icon name="check-circle" size={20} color="#10B981" />
                    <Text style={{ fontSize: 12, fontFamily: fonts.medium, color: '#10B981', flex: 1 }}>
                      Successfully extracted {selectedReport.totalJobsExtracted} job{selectedReport.totalJobsExtracted !== 1 ? 's' : ''} from {selectedReport.processedChannels} channel{selectedReport.processedChannels !== 1 ? 's' : ''}
                    </Text>
                  </View>
                )}

                {/* Cancelled Footer */}
                {selectedReport.status === 'cancelled' && (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, padding: 14, borderRadius: 10, backgroundColor: '#F59E0B15', borderWidth: 1, borderColor: '#F59E0B30', marginTop: 16 }}>
                    <Icon name="stop-circle" size={20} color="#F59E0B" />
                    <Text style={{ fontSize: 12, fontFamily: fonts.medium, color: '#F59E0B', flex: 1 }}>
                      Stopped by an admin after {selectedReport.processedChannels} of {selectedReport.totalChannels} channel{selectedReport.totalChannels !== 1 ? 's' : ''}
                      {selectedReport.totalJobsExtracted > 0 ? ` — ${selectedReport.totalJobsExtracted} job${selectedReport.totalJobsExtracted !== 1 ? 's' : ''} extracted before stopping` : ''}
                    </Text>
                  </View>
                )}
              </View>
            </ScrollView>
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 24,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
  },
  refreshButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  },
  refreshButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  autoRefreshButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
  },
  autoRefreshText: {
    fontSize: 12,
    fontWeight: '600',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 16,
  },
  statsContainer: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 24,
    flexWrap: 'wrap',
  },
  statCard: {
    flex: 1,
    minWidth: 140,
    padding: 20,
    borderRadius: 12,
    alignItems: 'center',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
      },
      android: {
        elevation: 3,
      },
      web: {
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      },
    }),
  },
  statIconWrapper: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 3,
  },
  statValue: {
    fontSize: 32,
    fontWeight: 'bold',
    marginTop: 4,
  },
  statLabel: {
    fontSize: 14,
    marginTop: 4,
    fontWeight: '500',
  },
  reportsContainer: {
    gap: 16,
  },
  tableContainer: {
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  tableHeader: {
    flexDirection: 'row',
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderBottomWidth: 2,
  },
  tableHeaderCell: {
    fontSize: 13,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    alignItems: 'center',
    minHeight: 60,
  },
  tableCell: {
    fontSize: 14,
    paddingHorizontal: 8,
  },
  statusColumn: {
    flex: 1.2,
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 20,
    borderWidth: 1.5,
    alignSelf: 'flex-start',
  },
  typeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    borderWidth: 2,
    minWidth: 90,
    alignSelf: 'flex-start',
  },
  typeBadgeText: {
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.3,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.3,
  },
  typeColumn: {
    flex: 1,
  },
  dateColumn: {
    flex: 1.8,
  },
  channelsColumn: {
    flex: 1,
    textAlign: 'center',
  },
  jobsColumn: {
    flex: 0.7,
    textAlign: 'center',
  },
  messagesColumn: {
    flex: 1,
    textAlign: 'center',
  },
  durationColumn: {
    flex: 0.9,
  },
  actionsColumn: {
    flex: 1,
    alignItems: 'center',
  },
  viewButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 16,
    paddingVertical: 9,
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  viewButtonText: {
    fontSize: 13,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  paginationWrapper: {
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  paginationContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  paginationButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  },
  paginationButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  paginationButtonDisabled: {
    opacity: 0.5,
  },
  paginationInfo: {
    alignItems: 'center',
  },
  paginationText: {
    fontSize: 15,
    fontWeight: '700',
  },
  paginationSubtext: {
    fontSize: 12,
    marginTop: 2,
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: 20,
  },
  modalContent: {
    width: '100%',
    maxWidth: 700,
    maxHeight: '90%',
    borderRadius: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.3,
    shadowRadius: 16,
    elevation: 10,
    overflow: 'hidden',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: 24,
    paddingBottom: 16,
  },
  modalHeaderContent: {
    flex: 1,
  },
  modalHeaderTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  modalIconWrapper: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: '800',
    marginBottom: 8,
    letterSpacing: -0.5,
  },
  sessionIdContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  sessionIdText: {
    fontSize: 11,
    fontFamily: 'monospace',
    letterSpacing: 0.5,
  },
  modalSubtitle: {
    fontSize: 12,
    fontFamily: 'monospace',
  },
  closeButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  modalBody: {
    padding: 24,
    paddingTop: 0,
    flex: 1,
  },
  statusBadgeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    padding: 20,
    borderRadius: 16,
    borderWidth: 2,
    marginBottom: 24,
  },
  statusIconWrapper: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 5,
  },
  statusTextContainer: {
    flex: 1,
  },
  statusBadgeTitle: {
    fontSize: 18,
    fontWeight: '800',
    marginBottom: 8,
    letterSpacing: -0.3,
  },
  statusMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },
  typeChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 12,
    borderWidth: 1.5,
  },
  typeChipText: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.4,
  },
  completedAtText: {
    fontSize: 12,
    fontWeight: '500',
  },
  statusBadgeSubtitle: {
    fontSize: 13,
    marginTop: 2,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 12,
  },
  statBox: {
    flex: 1,
    minWidth: '45%',
    padding: 18,
    borderRadius: 12,
  },
  statBoxHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  statBoxLabel: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  statBoxValue: {
    fontSize: 24,
    fontWeight: '800',
    letterSpacing: -0.5,
  },
  timelineSection: {
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '800',
    marginBottom: 16,
    letterSpacing: -0.3,
  },
  timelineContainer: {
    position: 'relative',
    paddingLeft: 8,
  },
  timelineLine: {
    position: 'absolute',
    left: 20,
    top: 20,
    bottom: 20,
    width: 2,
  },
  timelineItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
    marginBottom: 20,
    position: 'relative',
  },
  timelineDot: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 3,
    zIndex: 1,
  },
  timelineContent: {
    flex: 1,
    paddingTop: 8,
  },
  timelineLabel: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  timelineValue: {
    fontSize: 14,
    fontWeight: '600',
  },
  detailsRow: {
    marginBottom: 16,
  },
  detailsLabel: {
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 4,
  },
  detailsValue: {
    fontSize: 14,
  },
  errorsSection: {
    marginTop: 20,
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  errorsSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  errorBadge: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorsSectionTitle: {
    fontSize: 16,
    fontWeight: '800',
    flex: 1,
    letterSpacing: -0.3,
  },
  errorCount: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  errorCountText: {
    fontSize: 12,
    fontWeight: '800',
  },
  errorsContainer: {
    gap: 12,
  },
  errorItem: {
    padding: 16,
    borderRadius: 10,
    borderLeftWidth: 4,
  },
  errorItemHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 8,
  },
  errorIndex: {
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  errorText: {
    fontSize: 13,
    lineHeight: 20,
    fontFamily: 'monospace',
  },
  successFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    marginTop: 20,
    borderRadius: 12,
    borderWidth: 2,
  },
  successFooterText: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
    lineHeight: 20,
  },
  emptyState: {
    padding: 48,
    borderRadius: 12,
    alignItems: 'center',
  },
  emptyStateText: {
    fontSize: 18,
    fontWeight: '600',
    marginTop: 16,
  },
  emptyStateSubtext: {
    fontSize: 14,
    marginTop: 8,
  },
  reportCard: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
      },
      android: {
        elevation: 3,
      },
      web: {
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      },
    }),
  },
  reportHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  reportHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  reportHeaderInfo: {
    flex: 1,
  },
  reportTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  reportTitle: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  typeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  typeBadgeText: {
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'capitalize',
  },
  reportDate: {
    fontSize: 14,
  },
  reportStats: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  reportStat: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  reportStatText: {
    fontSize: 13,
  },
  reportDetails: {
    marginTop: 16,
  },
  detailsDivider: {
    height: 1,
    marginBottom: 16,
  },
  detailsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  detailsLabel: {
    fontSize: 14,
    fontWeight: '600',
  },
  detailsValue: {
    fontSize: 14,
    flex: 1,
    textAlign: 'right',
  },
  errorsSection: {
    marginTop: 16,
  },
  errorsSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  errorsSectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  errorItem: {
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
  },
  errorText: {
    fontSize: 13,
  },
});
